import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollText, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function Login() {
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const googleBtnRef = useRef(null);

  const handleGoogleCredential = async (response) => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/google", { credential: response.credential });
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Google sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return; // Google login not configured; email/password still works.

    const initGoogle = () => {
      if (!window.google || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "outline",
        size: "large",
        width: 320,
      });
    };

    if (window.google) {
      initGoogle();
    } else {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.onload = initGoogle;
      document.body.appendChild(script);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email, password } : { email, password, name };
      const { data } = await api.post(path, payload);
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden grain">
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/31650443/pexels-photo-31650443.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(2px)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />

      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="px-8 py-6 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-3">
            <ScrollText className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="font-serif text-xl tracking-tight">Ledger</span>
          </div>
          <span className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            Premium Invoicing
          </span>
        </header>

        <main className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-xl w-full">
            <p className="font-mont text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-6">
              — Est. 2026
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] tracking-tight mb-6">
              Invoicing,
              <br />
              <span className="italic text-neutral-400">refined.</span>
            </h1>
            <p className="text-neutral-400 text-base sm:text-lg mb-10 max-w-md leading-relaxed">
              A bespoke command center for elite operators. Two business profiles. GST automated. Premium documents, every time.
            </p>

            {GOOGLE_CLIENT_ID && (
              <div className="mb-6">
                <div ref={googleBtnRef} />
                <div className="flex items-center gap-4 mt-6 mb-2 max-w-sm">
                  <div className="h-px bg-white/10 flex-1" />
                  <span className="text-neutral-600 text-xs uppercase tracking-widest">or</span>
                  <div className="h-px bg-white/10 flex-1" />
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="max-w-sm space-y-4">
              {mode === "register" && (
                <Input
                  data-testid="name-input"
                  type="text"
                  placeholder="Full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="rounded-none bg-white/5 border-white/20 text-white placeholder:text-neutral-500"
                />
              )}
              <Input
                data-testid="email-input"
                type="email"
                placeholder="Email address"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none bg-white/5 border-white/20 text-white placeholder:text-neutral-500"
              />
              <Input
                data-testid="password-input"
                type="password"
                placeholder="Password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none bg-white/5 border-white/20 text-white placeholder:text-neutral-500"
              />
              {error && <p className="text-red-400 text-sm">{error}</p>}

              <Button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={loading}
                className="rounded-none bg-white text-black hover:bg-neutral-200 font-sans font-medium px-8 py-6 text-sm tracking-wide group w-full"
              >
                {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
                <ArrowRight className="w-4 h-4 ml-3 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
              </Button>

              <button
                type="button"
                onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
                className="text-neutral-400 text-sm underline underline-offset-4 hover:text-white"
              >
                {mode === "login" ? "Need an account? Register" : "Already have an account? Sign in"}
              </button>
            </form>

            <div className="mt-16 flex items-center gap-8 font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-600">
              <span>· Indian GST</span>
              <span>· Dual Profiles</span>
              <span>· PDF Export</span>
            </div>
          </div>
        </main>

        <footer className="px-8 py-6 border-t border-white/10 font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-600 flex justify-between">
          <span>Ledger / v1.0</span>
          <span>Self-hosted</span>
        </footer>
      </div>
    </div>
  );
}
